import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { BillStatus, dayjs } from "@boo/shared";
import { Button } from "@/components/ui/button";
import { useBillStore } from "./store/useBillStore";
import { formatBaht } from "./lib/billMath";
import { BILL_LIST_STATUS } from "./lib/billListStatus";
import ReceiptScanButton from "./components/ReceiptScanButton";

export default function BillsPage() {
  const navigate = useNavigate();
  const bills = useBillStore((s) => s.bills);
  const listLoading = useBillStore((s) => s.listLoading);
  const scanning = useBillStore((s) => s.scanning);
  const { loadBills, startNew, scanReceipt } = useBillStore.getState();

  useEffect(() => {
    async function load() {
      try {
        await loadBills();
      } catch {
        toast.error("Failed to load bills");
      }
    }
    void load();
  }, [loadBills]);

  async function handleScan(file: File) {
    startNew({});
    navigate("/bills/new");
    try {
      await scanReceipt(file);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not read the receipt",
      );
    }
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
          <Receipt className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">Bills</h1>
          <p className="text-sm text-muted-foreground">
            Split a meal by item and collect paybacks
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <ReceiptScanButton scanning={scanning} onFile={handleScan} />
        <Button
          variant="outline"
          onClick={() => {
            startNew({});
            navigate("/bills/new");
          }}
        >
          <Plus className="w-4 h-4 mr-2" /> Enter manually
        </Button>
      </div>

      {listLoading && bills.length === 0 && (
        <ListRowsSkeleton rows={4} variant="cards" />
      )}
      {!listLoading && bills.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No bills yet. Scan a receipt to start.
        </p>
      )}

      <ul className="space-y-2">
        {bills.map((b) => (
          <li key={b.id}>
            <Link
              to={`/bills/${b.id}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3.5 hover:border-primary/50 transition-colors"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-foreground">
                  {b.title}
                </div>
                <div className="text-xs text-muted-foreground">
                  {dayjs(b.date).format("D MMM YYYY")} · {b.personCount} people
                  {b.status === BillStatus.Finalized &&
                    b.friendCount > 0 &&
                    ` · ${b.paidCount}/${b.friendCount} paid`}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-foreground [font-variant-numeric:tabular-nums]">
                  {formatBaht(b.total)}
                </div>
                <span
                  className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${BILL_LIST_STATUS[b.listStatus].className}`}
                >
                  {BILL_LIST_STATUS[b.listStatus].label}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
